import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { api } from "../lib/api";
import {
  getApiNetworkSnapshot,
  recordApiFailure,
  subscribeToApiNetwork
} from "../lib/networkStatus";

const FAILURE_PROBE_COOLDOWN_MS = 30_000;
const PASSIVE_PROBE_COOLDOWN_MS = 60_000;

function formattedTime(value: number | null): string {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}

export function ConnectionStatus() {
  const apiNetwork = useSyncExternalStore(
    subscribeToApiNetwork,
    getApiNetworkSnapshot,
    getApiNetworkSnapshot
  );
  const [browserOnline, setBrowserOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine !== false
  );
  const [checking, setChecking] = useState(apiNetwork.reachability === "unknown");
  const probeRef = useRef<Promise<void> | null>(null);
  const probeControllerRef = useRef<AbortController | null>(null);
  const lastProbeAtRef = useRef(0);
  const deferredProbeRef = useRef<number | null>(null);

  const checkReachability = useCallback(
    function checkReachability(force = false, scheduleAfterCooldown = false): Promise<void> {
      if (!browserOnline) return Promise.resolve();

      const now = Date.now();
      const cooldown =
        apiNetwork.reachability === "unreachable"
          ? FAILURE_PROBE_COOLDOWN_MS
          : PASSIVE_PROBE_COOLDOWN_MS;
      const remainingCooldown = Math.max(0, cooldown - (now - lastProbeAtRef.current));
      const scheduleDeferredProbe = () => {
        if (!scheduleAfterCooldown || deferredProbeRef.current != null) return;
        deferredProbeRef.current = window.setTimeout(() => {
          deferredProbeRef.current = null;
          void checkReachability(true);
        }, remainingCooldown);
      };

      if (probeRef.current) {
        if (remainingCooldown > 0) scheduleDeferredProbe();
        return probeRef.current;
      }
      if (!force && remainingCooldown > 0) {
        scheduleDeferredProbe();
        return Promise.resolve();
      }

      if (deferredProbeRef.current != null) {
        window.clearTimeout(deferredProbeRef.current);
        deferredProbeRef.current = null;
      }

      lastProbeAtRef.current = now;
      setChecking(true);
      const controller = new AbortController();
      probeControllerRef.current = controller;

      const probe = api
        .health({ signal: controller.signal })
        .then(() => undefined)
        .catch(() => undefined)
        .finally(() => {
          if (probeControllerRef.current === controller) probeControllerRef.current = null;
          if (probeRef.current === probe) probeRef.current = null;
          setChecking(false);
        });

      probeRef.current = probe;
      return probe;
    },
    [apiNetwork.reachability, browserOnline]
  );

  useEffect(() => {
    if (!browserOnline) return;
    void checkReachability(true);
    // The initial probe and the browser's online transition are the only forced checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserOnline]);

  useEffect(() => {
    const handleOffline = () => {
      probeControllerRef.current?.abort();
      if (deferredProbeRef.current != null) {
        window.clearTimeout(deferredProbeRef.current);
        deferredProbeRef.current = null;
      }
      recordApiFailure("OFFLINE");
      setBrowserOnline(false);
      setChecking(false);
    };
    const handleOnline = () => {
      setChecking(true);
      setBrowserOnline(true);
    };
    const handlePassiveCheck = () => {
      if (document.visibilityState === "visible") void checkReachability();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handlePassiveCheck);
    document.addEventListener("visibilitychange", handlePassiveCheck);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handlePassiveCheck);
      document.removeEventListener("visibilitychange", handlePassiveCheck);
    };
  }, [checkReachability]);

  useEffect(() => {
    if (apiNetwork.reachability !== "reachable" || deferredProbeRef.current == null) return;
    window.clearTimeout(deferredProbeRef.current);
    deferredProbeRef.current = null;
  }, [apiNetwork.reachability]);

  useEffect(() => {
    if (!browserOnline || !apiNetwork.lastFailureAt) return;
    void checkReachability(false, true);
  }, [apiNetwork.lastFailureAt, browserOnline, checkReachability]);

  useEffect(
    () => () => {
      probeControllerRef.current?.abort();
      if (deferredProbeRef.current != null) {
        window.clearTimeout(deferredProbeRef.current);
        deferredProbeRef.current = null;
      }
    },
    []
  );

  const offline = !browserOnline || apiNetwork.lastErrorCode === "OFFLINE";
  const initialCheck = !offline && checking && apiNetwork.reachability === "unknown";
  const reconnecting = !offline && checking && apiNetwork.reachability === "unreachable";
  const unstable = !offline && !reconnecting && apiNetwork.reachability === "unreachable";
  const lastReached = formattedTime(apiNetwork.lastUpdatedAt);
  const statusClass = offline
    ? "connection-status-offline"
    : unstable
      ? "connection-status-unstable"
      : reconnecting || initialCheck
        ? "connection-status-reconnecting"
        : "connection-status-online";
  const longLabel = offline
    ? "Offline"
    : initialCheck
      ? "Checking connection…"
      : reconnecting
        ? "Reconnecting…"
        : unstable
          ? "Connection unstable"
          : lastReached
            ? `Server reached ${lastReached}`
            : "Checking connection…";
  const compactLabel = offline
    ? "Offline"
    : initialCheck
      ? "Checking…"
      : reconnecting
        ? "Reconnecting…"
        : unstable
          ? "Unstable"
          : lastReached
            ? `Connected ${lastReached}`
            : "Checking…";

  return (
    <div
      className={`connection-status ${statusClass}`}
      role="status"
      aria-live="polite"
      aria-label={longLabel}
      title={longLabel}
    >
      <span className="connection-status-dot" aria-hidden="true" />
      <span className="connection-status-label connection-status-label-full">{longLabel}</span>
      <span className="connection-status-label connection-status-label-compact">{compactLabel}</span>
    </div>
  );
}
